// This file is auto-generated by @hey-api/openapi-ts

import type { CancelablePromise } from './core/CancelablePromise';
import { OpenAPI } from './core/OpenAPI';
import { request as __request } from './core/request';
import type { AuthenticationLoginData, AuthenticationLoginResponse, AuthenticationRefreshTokenResponse, AuthenticationLogoutResponse, AuthenticationGetCurrentUserInfoResponse, BookingsListBookingsData, BookingsListBookingsResponse, BookingsCreateBookingData, BookingsCreateBookingResponse, BookingsGetBookingData, BookingsGetBookingResponse, BookingsUpdateBookingData, BookingsUpdateBookingResponse, BookingsDeleteBookingData, BookingsDeleteBookingResponse, BookingsCancelBookingData, BookingsCancelBookingResponse, CalendarGetWaterTaxiCalendarData, CalendarGetWaterTaxiCalendarResponse, CalendarGetKitchenCalendarData, CalendarGetKitchenCalendarResponse, CalendarGetKitchenWeekSummaryData, CalendarGetKitchenWeekSummaryResponse, CalendarGetKayakCalendarData, CalendarGetKayakCalendarResponse, DashboardGetDashboardSummaryData, DashboardGetDashboardSummaryResponse, GuestsCreateGuestResponse, GuestsGetGuestData, GuestsGetGuestResponse, GuestsUpdateGuestData, GuestsUpdateGuestResponse, LoginLoginAccessTokenData, LoginLoginAccessTokenResponse, LoginTestTokenResponse, LoginRecoverPasswordData, LoginRecoverPasswordResponse, LoginResetPasswordData, LoginResetPasswordResponse, LoginRecoverPasswordHtmlContentData, LoginRecoverPasswordHtmlContentResponse, PrivateCreateUserData, PrivateCreateUserResponse, RentalsCreateRentalResponse, RentalsListRentalsData, RentalsListRentalsResponse, RentalsCheckInRentalData, RentalsCheckInRentalResponse, TripsListTripsData, TripsListTripsResponse, TripsCreateTripResponse, TripsUpdateTripStatusData, TripsUpdateTripStatusResponse, UsersReadUsersData, UsersReadUsersResponse, UsersCreateUserData, UsersCreateUserResponse, UsersReadUserMeResponse, UsersDeleteUserMeResponse, UsersUpdateUserMeData, UsersUpdateUserMeResponse, UsersUpdatePasswordMeData, UsersUpdatePasswordMeResponse, UsersReadUserByIdData, UsersReadUserByIdResponse, UsersUpdateUserData, UsersUpdateUserResponse, UsersDeleteUserData, UsersDeleteUserResponse, UtilsTestEmailData, UtilsTestEmailResponse, UtilsHealthCheckResponse } from './types.gen';

export class AuthenticationService {
    /**
     * Login
     * Authenticate user and return JWT tokens in HTTP-only cookies.
     *
     * Args:
     * response: FastAPI response object for setting cookies
     * session: Database session
     * form_data: OAuth2 form with username (email) and password
     *
     * Returns:
     * User information on successful authentication
     *
     * Raises:
     * HTTPException: 401 if credentials are invalid
     * @param data The data for the request.
     * @param data.formData
     * @returns unknown Successful Response
     * @throws ApiError
     */
    public static login(data: AuthenticationLoginData): CancelablePromise<AuthenticationLoginResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/auth/login',
            formData: data.formData,
            mediaType: 'application/x-www-form-urlencoded',
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Refresh Token
     * Refresh access token using refresh token.
     *
     * Args:
     * request: FastAPI request object
     * response: FastAPI response object for setting cookies
     * session: Database session
     *
     * Returns:
     * Success message with new tokens in cookies
     *
     * Raises:
     * HTTPException: 401 if refresh token is invalid
     * @returns string Successful Response
     * @throws ApiError
     */
    public static refreshToken(): CancelablePromise<AuthenticationRefreshTokenResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/auth/refresh'
        });
    }
    
    /**
     * Logout
     * Logout user by clearing authentication cookies.
     *
     * Args:
     * response: FastAPI response object for clearing cookies
     *
     * Returns:
     * Success message
     * @returns Message Successful Response
     * @throws ApiError
     */
    public static logout(): CancelablePromise<AuthenticationLogoutResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/auth/logout'
        });
    }
    
    /**
     * Get Current User Info
     * Get current authenticated user information.
     *
     * Args:
     * current_user: The current authenticated user (injected via dependency)
     *
     * Returns:
     * Current user information
     *
     * Raises:
     * HTTPException: 401 if not authenticated or user is inactive
     * @returns UserPublic Successful Response
     * @throws ApiError
     */
    public static getCurrentUserInfo(): CancelablePromise<AuthenticationGetCurrentUserInfoResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/auth/me'
        });
    }
}

export class BookingsService {
    /**
     * List Bookings
     * List all bookings with filtering and pagination.
     *
     * Required Permission: LIST_BOOKINGS
     *
     * Allowed Roles:
     * - System Administrator
     * - Manager
     * - Booking Coordinator
     * - Kitchen Staff (view-only)
     *
     * Query Parameters:
     * - search: Filter by guest name or booking ID (partial match, case-insensitive)
     * - date_from: Filter arrivals on or after this date
     * - date_to: Filter arrivals on or before this date
     * - status: Filter by booking status (pending, confirmed, cancelled, completed)
     * - limit: Page size (default 20, max 100)
     * - offset: Pagination offset
     * @param data The data for the request.
     * @param data.search Search by guest name or booking ID (partial match)
     * @param data.dateFrom Filter arrivals on or after this date
     * @param data.dateTo Filter arrivals on or before this date
     * @param data.status Filter by booking status
     * @param data.limit Page size
     * @param data.offset Pagination offset
     * @returns BookingListResponse Successful Response
     * @throws ApiError
     */
    public static listBookings(data: BookingsListBookingsData = {}): CancelablePromise<BookingsListBookingsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/bookings',
            query: {
                search: data.search,
                date_from: data.dateFrom,
                date_to: data.dateTo,
                status: data.status,
                limit: data.limit,
                offset: data.offset
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Create Booking
     * Create a new booking and its associated guest, transport, rental, and dietary records.
     *
     * If a guest with the provided email exists, update their contact info; otherwise create a new guest. Create a Booking with status PENDING, then create any provided WaterTaxiTrip and KayakRental records. If the booking is for a lodge or mixed stay, create DietaryRequirement records. The function commits the transaction and returns a summary of the created booking.
     *
     * Returns:
     * BookingResponse: Contains the created booking's `id`, `guest_id`, `booking_type`, `arrival_date`, `departure_date`, `status`, `created_at`, and a success message.
     * @param data The data for the request.
     * @param data.requestBody
     * @returns BookingResponse Successful Response
     * @throws ApiError
     */
    public static createBooking(data: BookingsCreateBookingData): CancelablePromise<BookingsCreateBookingResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/bookings',
            body: data.requestBody,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Get Booking
     * Retrieve a booking by ID including guest, water taxi trips, kayak rentals, and dietary requirements.
     *
     * Requires `VIEW_BOOKING` permission; allowed roles: System Administrator, Manager, Booking Coordinator, Kitchen Staff (view-only).
     *
     * Returns:
     * BookingDetailResponse: Booking fields and related `guest`, `water_taxi_trips`, `kayak_rentals`, and `dietary_requirements`.
     * @param data The data for the request.
     * @param data.bookingId
     * @returns BookingDetailResponse Successful Response
     * @throws ApiError
     */
    public static getBooking(data: BookingsGetBookingData): CancelablePromise<BookingsGetBookingResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/bookings/{booking_id}',
            path: {
                booking_id: data.bookingId
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Update Booking
     * Replace an existing booking and its associated guest, water taxi trips, kayak rentals, and dietary requirements with the provided data.
     *
     * Requires `UPDATE_BOOKING` permission. Raises an HTTP 404 error if no booking exists with the given `booking_id`.
     *
     * Returns:
     * BookingUpdateResponse: Updated booking summary including `id`, `guest_id`, `booking_type`, `arrival_date`, `departure_date`, `number_of_guests`, `status`, `updated_at`, and a success message.
     * @param data The data for the request.
     * @param data.bookingId
     * @param data.requestBody
     * @returns BookingUpdateResponse Successful Response
     * @throws ApiError
     */
    public static updateBooking(data: BookingsUpdateBookingData): CancelablePromise<BookingsUpdateBookingResponse> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/v1/bookings/{booking_id}',
            path: {
                booking_id: data.bookingId
            },
            body: data.requestBody,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Delete Booking
     * Delete a booking (hard delete).
     *
     * Required Permission: DELETE_BOOKING
     *
     * Allowed Roles:
     * - System Administrator
     * - Booking Coordinator
     * @param data The data for the request.
     * @param data.bookingId
     * @returns void Successful Response
     * @throws ApiError
     */
    public static deleteBooking(data: BookingsDeleteBookingData): CancelablePromise<BookingsDeleteBookingResponse> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/v1/bookings/{booking_id}',
            path: {
                booking_id: data.bookingId
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Cancel Booking
     * Mark the specified booking as cancelled and record the cancellation timestamp.
     *
     * Raises:
     * HTTPException: 404 if no booking exists with the given `booking_id`.
     *
     * Returns:
     * BookingCancelResponse: Contains the booking `id`, updated `status` value ("CANCELLED"), `cancelled_at` timestamp, and a human-readable `message`.
     * @param data The data for the request.
     * @param data.bookingId
     * @returns BookingCancelResponse Successful Response
     * @throws ApiError
     */
    public static cancelBooking(data: BookingsCancelBookingData): CancelablePromise<BookingsCancelBookingResponse> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/v1/bookings/{booking_id}/cancel',
            path: {
                booking_id: data.bookingId
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
}

export class CalendarService {
    /**
     * Get Water Taxi Calendar
     * Produce the water taxi schedule for a given date, including scheduled runs and custom pickups with passenger lists, kayak details, and per-time-slot capacity metrics.
     *
     * Parameters:
     * requested_date (date | None): Date to fetch calendar for (ISO format YYYY-MM-DD). If omitted, defaults to today's date in Pacific time.
     *
     * Returns:
     * APIResponse: Payload with `date` (ISO string), `summary` (WaterTaxiDailySummary), `trips` (list of trip entries containing trip/booking IDs, type, time, route, passenger and kayak details, notes, and capacity fields), and `last_updated` (ISO datetime).
     * @param data The data for the request.
     * @param data.requestedDate Date to fetch calendar for (ISO format: YYYY-MM-DD)
     * @returns APIResponse Successful Response
     * @throws ApiError
     */
    public static getWaterTaxiCalendar(data: CalendarGetWaterTaxiCalendarData = {}): CancelablePromise<CalendarGetWaterTaxiCalendarResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/calendar/water-taxi',
            query: {
                requested_date: data.requestedDate
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Get Kitchen Calendar
     * Return the kitchen calendar for a given date with meal breakdowns, dietary groupings, and allergy alerts.
     *
     * Produces per-meal (breakfast, lunch, dinner) attendee lists grouped by dietary requirement, allergy alerts, and a daily summary for the requested date. Camping guests appear only for their selected arrival/departure lunches; mixed stays include only their persisted lodge portions.
     *
     * Returns:
     * APIResponse: Response containing KitchenCalendarResponse data with keys `date`, `summary`, `meals`, and `last_updated`.
     * @param data The data for the request.
     * @param data.requestedDate Date to fetch kitchen calendar for (ISO format: YYYY-MM-DD)
     * @returns APIResponse Successful Response
     * @throws ApiError
     */
    public static getKitchenCalendar(data: CalendarGetKitchenCalendarData = {}): CancelablePromise<CalendarGetKitchenCalendarResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/calendar/kitchen',
            query: {
                requested_date: data.requestedDate
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Get Kitchen Week Summary
     * Produce a 7-day kitchen summary starting from `start_date`, with per-day meal counts, dietary breakdowns, and severe allergy totals.
     *
     * Parameters:
     * start_date (date | None): Start date of the week (ISO format YYYY-MM-DD). If omitted, defaults to the Monday of the current week.
     *
     * Returns:
     * dict: A serialized KitchenWeekSummaryResponse (via `model_dump`) containing:
     * - start_date: ISO date string for the week's Monday,
     * - days: list of KitchenDaySummary entries (each includes breakfast/lunch/dinner guest counts, dietary_breakdown, severe_allergy_count),
     * - last_updated: ISO timestamp string.
     * @param data The data for the request.
     * @param data.startDate Start date of the week (ISO format: YYYY-MM-DD, defaults to current week Monday)
     * @returns APIResponse Successful Response
     * @throws ApiError
     */
    public static getKitchenWeekSummary(data: CalendarGetKitchenWeekSummaryData = {}): CancelablePromise<CalendarGetKitchenWeekSummaryResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/calendar/kitchen/week',
            query: {
                start_date: data.startDate
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Get Kayak Calendar
     * Return the kayak rental calendar for a given date.
     *
     * @returns APIResponse containing a KayakCalendarResponse payload with keys:
     * - date: ISO date string for the requested day
     * - summary: KayakDailySummary (total_out, available, returning_today, pickups_today)
     * - pickups: list of KayakRentalEntry objects for rentals picked up that day, including guest_type
     * - returns: list of KayakRentalEntry objects for rentals returned that day, including guest_type
     * - inventory: list of KayakEquipmentInventory items (type, size, label, total, rented, available)
     * - last_updated: ISO timestamp of when the calendar was generated
     * @param data The data for the request.
     * @param data.requestedDate Date to fetch kayak calendar for (ISO format: YYYY-MM-DD)
     * @returns APIResponse Successful Response
     * @throws ApiError
     */
    public static getKayakCalendar(data: CalendarGetKayakCalendarData = {}): CancelablePromise<CalendarGetKayakCalendarResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/calendar/kayak',
            query: {
                requested_date: data.requestedDate
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
}

export class DashboardService {
    /**
     * Get Dashboard Summary
     * Return the manager dashboard summary for a given date.
     *
     * Includes today's operational metrics, attention items, and a tomorrow preview.
     * @param data The data for the request.
     * @param data.requestedDate Date to fetch dashboard for (ISO format: YYYY-MM-DD)
     * @returns APIResponse Successful Response
     * @throws ApiError
     */
    public static getDashboardSummary(data: DashboardGetDashboardSummaryData = {}): CancelablePromise<DashboardGetDashboardSummaryResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/dashboard/summary',
            query: {
                requested_date: data.requestedDate
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
}

export class GuestsService {
    /**
     * Create Guest
     * Create a new guest.
     *
     * Required Permission: CREATE_GUEST
     *
     * Allowed Roles:
     * - System Administrator
     * - Manager
     * - Booking Coordinator
     * @returns unknown Successful Response
     * @throws ApiError
     */
    public static createGuest(): CancelablePromise<GuestsCreateGuestResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/guests'
        });
    }
    
    /**
     * Get Guest
     * Get a specific guest by ID.
     *
     * Required Permission: VIEW_GUEST
     *
     * Allowed Roles:
     * - System Administrator
     * - Manager
     * - Booking Coordinator
     * - Kitchen Staff (view-only)
     * @param data The data for the request.
     * @param data.guestId
     * @returns unknown Successful Response
     * @throws ApiError
     */
    public static getGuest(data: GuestsGetGuestData): CancelablePromise<GuestsGetGuestResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/guests/{guest_id}',
            path: {
                guest_id: data.guestId
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Update Guest
     * Update a guest.
     *
     * Required Permission: UPDATE_GUEST
     *
     * Allowed Roles:
     * - System Administrator
     * - Manager
     * - Booking Coordinator
     * @param data The data for the request.
     * @param data.guestId
     * @returns unknown Successful Response
     * @throws ApiError
     */
    public static updateGuest(data: GuestsUpdateGuestData): CancelablePromise<GuestsUpdateGuestResponse> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/v1/guests/{guest_id}',
            path: {
                guest_id: data.guestId
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
}

export class LoginService {
    /**
     * Login Access Token
     * OAuth2 compatible token login, get an access token for future requests
     * @param data The data for the request.
     * @param data.formData
     * @returns Token Successful Response
     * @throws ApiError
     */
    public static loginAccessToken(data: LoginLoginAccessTokenData): CancelablePromise<LoginLoginAccessTokenResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/login/access-token',
            formData: data.formData,
            mediaType: 'application/x-www-form-urlencoded',
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Test Token
     * Return the authenticated user's public profile.
     *
     * Returns:
     * current_user (UserPublic): The authenticated user as a public-facing user object.
     * @returns UserPublic Successful Response
     * @throws ApiError
     */
    public static testToken(): CancelablePromise<LoginTestTokenResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/login/test-token'
        });
    }
    
    /**
     * Recover Password
     * Password Recovery
     * @param data The data for the request.
     * @param data.email
     * @returns Message Successful Response
     * @throws ApiError
     */
    public static recoverPassword(data: LoginRecoverPasswordData): CancelablePromise<LoginRecoverPasswordResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/password-recovery/{email}',
            path: {
                email: data.email
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Reset Password
     * Reset password
     * @param data The data for the request.
     * @param data.requestBody
     * @returns Message Successful Response
     * @throws ApiError
     */
    public static resetPassword(data: LoginResetPasswordData): CancelablePromise<LoginResetPasswordResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/reset-password/',
            body: data.requestBody,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Recover Password Html Content
     * HTML Content for Password Recovery
     * @param data The data for the request.
     * @param data.email
     * @returns string Successful Response
     * @throws ApiError
     */
    public static recoverPasswordHtmlContent(data: LoginRecoverPasswordHtmlContentData): CancelablePromise<LoginRecoverPasswordHtmlContentResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/password-recovery-html-content/{email}',
            path: {
                email: data.email
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
}

export class PrivateService {
    /**
     * Create User
     * Create a new user.
     * @param data The data for the request.
     * @param data.requestBody
     * @returns UserPublic Successful Response
     * @throws ApiError
     */
    public static createUser(data: PrivateCreateUserData): CancelablePromise<PrivateCreateUserResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/private/users/',
            body: data.requestBody,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error'
            }
        });
    }
}

export class RentalsService {
    /**
     * Create Rental
     * Create a new kayak rental.
     *
     * Required Permission: CREATE_RENTAL
     *
     * Allowed Roles:
     * - System Administrator
     * - Manager
     * - Dock Staff
     * - Kayak Staff
     * @returns unknown Successful Response
     * @throws ApiError
     */
    public static createRental(): CancelablePromise<RentalsCreateRentalResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/rentals'
        });
    }
    
    /**
     * List Rentals
     * List all kayak rentals.
     *
     * Required Permission: LIST_RENTALS
     *
     * Allowed Roles:
     * - System Administrator
     * - Manager
     * - Dock Staff
     * - Kayak Staff
     * - Kitchen Staff (view-only)
     * @param data The data for the request.
     * @param data.skip
     * @param data.limit
     * @returns unknown Successful Response
     * @throws ApiError
     */
    public static listRentals(data: RentalsListRentalsData = {}): CancelablePromise<RentalsListRentalsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/rentals',
            query: {
                skip: data.skip,
                limit: data.limit
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Check In Rental
     * Check in a kayak rental.
     *
     * Required Permission: CHECK_IN_RENTAL
     *
     * Allowed Roles:
     * - System Administrator
     * - Manager
     * - Dock Staff
     * - Kayak Staff
     * @param data The data for the request.
     * @param data.rentalId
     * @returns unknown Successful Response
     * @throws ApiError
     */
    public static checkInRental(data: RentalsCheckInRentalData): CancelablePromise<RentalsCheckInRentalResponse> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/v1/rentals/{rental_id}/check-in',
            path: {
                rental_id: data.rentalId
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
}

export class TripsService {
    /**
     * List Trips
     * List all water taxi trips.
     *
     * Required Permission: LIST_TRIPS
     *
     * Allowed Roles:
     * - System Administrator
     * - Manager
     * - Dock Staff
     * - Kitchen Staff (view-only)
     * @param data The data for the request.
     * @param data.skip
     * @param data.limit
     * @returns unknown Successful Response
     * @throws ApiError
     */
    public static listTrips(data: TripsListTripsData = {}): CancelablePromise<TripsListTripsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/trips',
            query: {
                skip: data.skip,
                limit: data.limit
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Create Trip
     * Create a new water taxi trip.
     *
     * Required Permission: CREATE_TRIP
     *
     * Allowed Roles:
     * - System Administrator
     * - Manager
     * - Dock Staff
     * @returns unknown Successful Response
     * @throws ApiError
     */
    public static createTrip(): CancelablePromise<TripsCreateTripResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/trips'
        });
    }
    
    /**
     * Update Trip Status
     * Update the status of a water taxi trip.
     *
     * Required Permission: UPDATE_TRIP_STATUS
     *
     * Allowed Roles:
     * - System Administrator
     * - Manager
     * - Dock Staff
     * @param data The data for the request.
     * @param data.tripId
     * @returns unknown Successful Response
     * @throws ApiError
     */
    public static updateTripStatus(data: TripsUpdateTripStatusData): CancelablePromise<TripsUpdateTripStatusResponse> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api/v1/trips/{trip_id}/status',
            path: {
                trip_id: data.tripId
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
}

export class UsersService {
    /**
     * Read Users
     * Retrieve users.
     * @param data The data for the request.
     * @param data.skip
     * @param data.limit
     * @param data.role
     * @param data.isActive
     * @returns UsersPublic Successful Response
     * @throws ApiError
     */
    public static readUsers(data: UsersReadUsersData = {}): CancelablePromise<UsersReadUsersResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/users/',
            query: {
                skip: data.skip,
                limit: data.limit,
                role: data.role,
                is_active: data.isActive
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Create User
     * Create new user.
     * @param data The data for the request.
     * @param data.requestBody
     * @returns UserPublic Successful Response
     * @throws ApiError
     */
    public static createUser(data: UsersCreateUserData): CancelablePromise<UsersCreateUserResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/users/',
            body: data.requestBody,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Read User Me
     * Retrieve the user object for the current active session.
     *
     * Returns:
     * The current authenticated active user.
     * @returns UserPublic Successful Response
     * @throws ApiError
     */
    public static readUserMe(): CancelablePromise<UsersReadUserMeResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/users/me'
        });
    }
    
    /**
     * Delete User Me
     * Delete the current active user's account.
     *
     * Raises:
     * HTTPException: with status code 403 if the current user is a superuser.
     *
     * Returns:
     * Message: Confirmation message stating the user was deleted successfully.
     * @returns Message Successful Response
     * @throws ApiError
     */
    public static deleteUserMe(): CancelablePromise<UsersDeleteUserMeResponse> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/v1/users/me'
        });
    }
    
    /**
     * Update User Me
     * Update the current user's profile with the provided fields.
     *
     * Parameters:
     * user_in (UserUpdateMe): Fields to update; only values present on `user_in` are applied.
     *
     * Returns:
     * User: The updated user model instance.
     *
     * Raises:
     * HTTPException: With status code 409 if another user already has the provided email.
     * @param data The data for the request.
     * @param data.requestBody
     * @returns UserPublic Successful Response
     * @throws ApiError
     */
    public static updateUserMe(data: UsersUpdateUserMeData): CancelablePromise<UsersUpdateUserMeResponse> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/v1/users/me',
            body: data.requestBody,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Update Password Me
     * Update the current user's password.
     *
     * Parameters:
     * body (UpdatePassword): Contains `current_password` and `new_password`.
     *
     * Returns:
     * Message: Success message confirming the password was updated.
     *
     * Raises:
     * HTTPException: 400 if the provided current password is incorrect.
     * HTTPException: 400 if the new password is the same as the current password.
     * @param data The data for the request.
     * @param data.requestBody
     * @returns Message Successful Response
     * @throws ApiError
     */
    public static updatePasswordMe(data: UsersUpdatePasswordMeData): CancelablePromise<UsersUpdatePasswordMeResponse> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/v1/users/me/password',
            body: data.requestBody,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Read User By Id
     * Retrieve a user by UUID, allowing the same user to access their own record or permitting superusers to access any user.
     *
     * Parameters:
     * user_id (uuid.UUID): UUID of the user to retrieve.
     *
     * Returns:
     * The requested User object if found, or `None` if no user exists with the given `user_id`.
     *
     * Raises:
     * HTTPException: 403 if the requester is not the same user and is not a superuser.
     * @param data The data for the request.
     * @param data.userId
     * @returns UserPublic Successful Response
     * @throws ApiError
     */
    public static readUserById(data: UsersReadUserByIdData): CancelablePromise<UsersReadUserByIdResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/users/{user_id}',
            path: {
                user_id: data.userId
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Update User
     * Update a user.
     * @param data The data for the request.
     * @param data.userId
     * @param data.requestBody
     * @returns UserPublic Successful Response
     * @throws ApiError
     */
    public static updateUser(data: UsersUpdateUserData): CancelablePromise<UsersUpdateUserResponse> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/v1/users/{user_id}',
            path: {
                user_id: data.userId
            },
            body: data.requestBody,
            mediaType: 'application/json',
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Delete User
     * Deactivate a user by ID (soft delete).
     *
     * Parameters:
     * user_id (uuid.UUID): The UUID of the user to deactivate.
     *
     * Returns:
     * Message: Object containing a success message when deactivation completes.
     *
     * Raises:
     * HTTPException: 404 if the user does not exist.
     * HTTPException: 403 if the current user attempts to deactivate themselves.
     * @param data The data for the request.
     * @param data.userId
     * @returns Message Successful Response
     * @throws ApiError
     */
    public static deleteUser(data: UsersDeleteUserData): CancelablePromise<UsersDeleteUserResponse> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/v1/users/{user_id}',
            path: {
                user_id: data.userId
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
}

export class UtilsService {
    /**
     * Test Email
     * Test emails.
     * @param data The data for the request.
     * @param data.emailTo
     * @returns Message Successful Response
     * @throws ApiError
     */
    public static testEmail(data: UtilsTestEmailData): CancelablePromise<UtilsTestEmailResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/utils/test-email/',
            query: {
                email_to: data.emailTo
            },
            errors: {
                422: 'Validation Error'
            }
        });
    }
    
    /**
     * Health Check
     * @returns boolean Successful Response
     * @throws ApiError
     */
    public static healthCheck(): CancelablePromise<UtilsHealthCheckResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/utils/health-check/'
        });
    }
}